import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { useTheme, buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import fr from 'facet-rest';
import useMobileLayout from 'Hooks/useMobileLayout';
import ProfileSubpage from 'Views/Page/Content/Templates/Profile/Templates/ProfileSubpage';
import { root, content } from 'Views/Page/Content/Templates/Profile/Templates/ProfileTemplate/Subpages/subpageClasses';
import Paper from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Paper';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import { Link } from '@material-ui/core';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import { FDBox } from '@facet-wealth/design';
import BillingDetailsProfileSubpageRenewalDatePicker
  from 'Views/Page/Content/Templates/Profile/Templates/ProfileTemplate/Subpages/BillingDetailsProfileSubpage/BillingDetailsProfileSubpageRenewalDatePicker/BillingDetailsProfileSubpageRenewalDatePicker';
import { DataValueMap } from 'Helpers/Testing';
import BillingDetailsTable from './BillingDetailsTable';

const useStyles = buildUseStyles('BillingDetailsProfileSubpage', (theme) => ({
  root: root(theme),
  content: content(theme),
  planContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    width: '100%',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.facet.lightGray,
  },
  disclaimer: {
    margin: theme.spacing(1),
  },
  tableContainer: {
    width: '100%',
  },
}));


const BillingDetailsProfileSubpage = ({
  className,
  resource,
}) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const { loadModalCollectionByLink } = useModalContext();
  const profilePageRelationship = resource.relationship(fr.relationships.householdMembershipPage.name);
  const isMobile = useMobileLayout();
  const backLabel = profilePageRelationship?.meta('prompt');
  const backLink = profilePageRelationship?.link('related');
  const isChurned = resource.attr('churned') === 'true';
  const isEscalated = resource.attr('escalated') === 'true';
  const isChurnedOrEscalated = isChurned || isEscalated;

  const paymentText = `${resource.attr('lastPaymentInfo')} ${resource.attr('nextPaymentInfo')}`;

  return (
    <ProfileSubpage
      className={classNames(classes.root, className, { [classes.mobile]: isMobile })}
      title={resource.attr('title')}
      backLabel={backLabel}
      backLink={backLink}
    >
      <Box className={classes.content}>
        <Paper
          className={classes.planContainer}
          rounded
        >
          <Typography
            className={classes.subtitle}
            variant={'h5'}
            color={'naturalGreen'}
            weight={'bold'}
          >
            {resource.attr('subtitle')}
          </Typography>
          <Typography
            className={classNames(classes.description, { [classes.churnedEscalatedDescription]: isChurnedOrEscalated })}
            variant={'h5'}
            color={'darkGray'}
            weight={'medium'}
          >
            {resource.attr('annualMembershipFeeInfo')}
          </Typography>
          <BillingDetailsProfileSubpageRenewalDatePicker resource={resource} />
          <Typography
            className={classes.description}
            variant={'h5'}
            color={'darkGray'}
            weight={'medium'}
            {...DataValueMap.init('billing-details-profile-subpage-payment-text').build()}
          >
            {paymentText}
            {resource.withRelationship(fr.relationships.requestChurnCancel.name,
              (rel) => (
                <Link
                  component={'button'}
                  variant={'h5'}
                  className={classes.link}
                  onClick={() => loadModalCollectionByLink(rel.link('related').url())}
                >
                  {'Here'}
                </Link>
              ),
              () => null)}
          </Typography>
        </Paper>
        <Typography
          className={classes.disclaimer}
          variant={'caption'}
          color={'primary'}
        >
          {resource.attr('billingDisclaimer')}
        </Typography>
        <FDBox width={'100%'}>
          <BillingDetailsTable
            className={classes.tableContainer}
            billingDetailsPageResource={resource}
          />
        </FDBox>
      </Box>
    </ProfileSubpage>
  );
};

BillingDetailsProfileSubpage.defaultProps = {
  className: '',
};

BillingDetailsProfileSubpage.propTypes = {
  className: PropTypes.string,
  resource: PropTypes.instanceOf(fr.Resource).isRequired,
};

export default BillingDetailsProfileSubpage;
